// src/admin/analytics/AnalyticsReportStatus.js
import React from "react";
import { Button, Grid, Card, Table, Page } from "tabler-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AnalyticsDownloadPopup from "./AnalyticsDownloadPopup";
import { connect } from "react-redux";
import authActions from "../../redux/auth/action";

const TIMEZONE = "Asia/Kolkata";

class AnalyticsReportStatus extends React.Component {
  state = {
    jobId: null,
    latestJobs: [],
    latestSchedules: [],
    openPopup: false,
  };

  componentDidMount() {
    this.fetchJobs();
    this.fetchSchedules();
  }

  // Helper to safely get token
  getToken = () => localStorage.getItem("jwtToken") || "";

  // [NEW METHOD] Securely download file with Auth header
  downloadFile = (jobId) => {
    fetch(
      `${process.env.REACT_APP_apiUrl}analytics/download-analytics/${jobId}/file`,
      {
        headers: { Authorization: `Bearer ${this.getToken()}` },
      },
    )
      .then((response) => {
        if (response.status === 401) throw new Error("Unauthorized");
        if (response.ok) return response.blob();
        throw new Error("Download failed");
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `analytics_report_${jobId}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(() => toast.error("Download failed or Unauthorized"));
  };

  fetchJobs = () => {
    fetch(`${process.env.REACT_APP_apiUrl}analytics/analyticsJobStatus`, {
      headers: { Authorization: `Bearer ${this.getToken()}` }, // Updated
    })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data.jobs)) {
          this.setState({ latestJobs: data.jobs });
        }
      })
      .catch(console.error);
  };

  fetchSchedules = () => {
    fetch(`${process.env.REACT_APP_apiUrl}analytics/listAnalyticsSchedules`, {
      headers: { Authorization: `Bearer ${this.getToken()}` }, // Updated
    })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data.schedules)) {
          this.setState({ latestSchedules: data.schedules });
        }
      })
      .catch(console.error);
  };

  startExportJob = (args, scheduleConfig) => {
    if (scheduleConfig.mode === "once") {
      // one-time
      const start = args.startDate.toISOString().slice(0, 10);
      const end = args.endDate.toISOString().slice(0, 10);
      fetch(
        `${process.env.REACT_APP_apiUrl}analytics/download-analytics` +
          `?fromDate=${start}&toDate=${end}&timezone=${TIMEZONE}`,
        {
          headers: { Authorization: `Bearer ${this.getToken()}` }, // Updated Header
        },
      )
        .then((r) => r.json())
        .then((data) => {
          if (data.jobId) {
            toast.success("Export job requested.");
            // **replace** `jobId && this.fetchJobs();` with:
            if (data.jobId) {
              this.setState({ jobId: data.jobId }, this.fetchJobs);
            }
          } else {
            toast.error("Failed to request export job.");
          }
        })
        .catch(() => toast.error("Network error requesting export"));
    } else {
      // scheduled
      fetch(
        `${process.env.REACT_APP_apiUrl}analytics/scheduleAnalyticsReport`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.getToken()}`,
          },
          body: JSON.stringify(scheduleConfig),
        },
      )
        .then((r) => r.json())
        .then((data) => {
          if (data.scheduleId) {
            toast.success("Analytics schedule created.");
            this.fetchSchedules();
          } else {
            toast.error("Failed to schedule analytics report.");
          }
        })
        .catch(() => toast.error("Network error scheduling report"));
    }
  };

  deleteSchedule = (id) => {
    fetch(
      `${process.env.REACT_APP_apiUrl}analytics/scheduleAnalyticsReport/${id}`,
      {
        method: "DELETE",
        headers: { Authorization: `Bearer ${this.getToken()}` },
      }, // Added Header
    )
      .then((r) => {
        if (r.ok) {
          toast.success("Schedule deleted.");
          this.fetchSchedules();
        } else {
          toast.error("Failed to delete schedule.");
        }
      })
      .catch(() => toast.error("Network error deleting schedule"));
  };

  render() {
    const { latestJobs, latestSchedules, jobId, openPopup } = this.state;

    return (
      <Page.Content>
        <ToastContainer autoClose={3000} position="bottom-right" />

        <Grid.Row>
          <Grid.Col lg={12}>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <Card.Title>Analytics Export</Card.Title>
                <div>
                  {/* always-visible Refresh */}
                  <Button
                    color="secondary"
                    className="mr-2"
                    onClick={this.fetchJobs}
                  >
                    Refresh
                  </Button>
                  {/* Download popup trigger */}
                  <Button
                    color="primary"
                    onClick={() => this.setState({ openPopup: true })}
                  >
                    Download
                  </Button>
                </div>
              </Card.Header>

              <Card.Body>
                <h5>One-Time Jobs</h5>
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColHeader>Job ID</Table.ColHeader>
                      <Table.ColHeader>Status</Table.ColHeader>
                      <Table.ColHeader>Created At</Table.ColHeader>
                      <Table.ColHeader>Action</Table.ColHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {latestJobs.map((job, i) => (
                      <Table.Row key={i}>
                        <Table.Col>{job.jobId}</Table.Col>
                        <Table.Col>{job.status}</Table.Col>
                        <Table.Col>
                          {new Date(job.createdAt).toLocaleString()}
                        </Table.Col>
                        <Table.Col>
                          {job.status === "completed" && (
                            <Button
                              color="success"
                              size="sm"
                              onClick={() => this.downloadFile(job.jobId)}
                            >
                              Download
                            </Button>
                          )}
                        </Table.Col>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>

                <h5 className="mt-4">Recurring Schedules</h5>
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColHeader>Schedule ID</Table.ColHeader>
                      <Table.ColHeader>Emails</Table.ColHeader>
                      <Table.ColHeader>Scope / Frequency</Table.ColHeader>
                      <Table.ColHeader>Created At</Table.ColHeader>
                      <Table.ColHeader>Status</Table.ColHeader>
                      <Table.ColHeader>Action</Table.ColHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {latestSchedules.map((s, i) => (
                      <Table.Row key={i}>
                        <Table.Col>{s._id}</Table.Col>
                        <Table.Col>
                          {(Array.isArray(s.emails)
                            ? s.emails.join(", ")
                            : "") || "—"}
                        </Table.Col>
                        <Table.Col>
                          {s.scopeMode === "fixed"
                            ? `${s.from} → ${s.to}`
                            : `Last ${s.scopeValue} ${s.scopeUnit}`}
                          <br />
                          {`Every ${s.freqValue} ${s.freqUnit}`}
                        </Table.Col>
                        <Table.Col>
                          {new Date(s.createdAt).toLocaleString()}
                        </Table.Col>
                        <Table.Col>
                          {s.isActive ? "Active" : "Paused"}
                        </Table.Col>
                        <Table.Col>
                          <Button
                            color="danger"
                            size="sm"
                            onClick={() => this.deleteSchedule(s._id)}
                          >
                            Delete
                          </Button>
                        </Table.Col>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Card.Body>
            </Card>
          </Grid.Col>
        </Grid.Row>

        <AnalyticsDownloadPopup
          openInfoPopup={{ open: openPopup }}
          onChange={(open) => this.setState({ openPopup: open })}
          onExportJobStart={this.startExportJob}
        />
      </Page.Content>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
});
const mapDispatchToProps = (dispatch) => ({
  logOut: () => dispatch(authActions.authLogout()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AnalyticsReportStatus);
